import { useQuery, useMutation } from '@tanstack/react-query';
import { orpc, rpcClient, useInvalidateRpcQueryFn } from '@/api/rpc/client';
import { useToast } from '@/context/ToastContext';
import type { SandboxConfigBodyInput } from '@erp/rpc';

function useInvalidateSandboxConfigs() {
  return useInvalidateRpcQueryFn((o) => [o.org.config.sandbox.list.key()]);
}

export function useSandboxConfigs() {
  return useQuery({
    ...orpc.org.config.sandbox.list.queryOptions({ input: undefined }),
  });
}

export function useCreateSandboxConfig(options?: { onSuccess?: () => void }) {
  const invalidate = useInvalidateSandboxConfigs();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (input: { name: string; config: SandboxConfigBodyInput }) =>
      rpcClient.org.config.sandbox.create(input),
    onSuccess: () => {
      showToast({
        title: 'Configuration created',
        message: 'Sandbox configuration has been saved.',
        color: 'success',
      });
      invalidate({});
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      showToast({
        title: 'Failed to create configuration',
        message: error.message,
        color: 'error',
      });
    },
  });
}

export function useUpdateSandboxConfig(options?: { onSuccess?: () => void }) {
  const invalidate = useInvalidateSandboxConfigs();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (input: {
      id: string;
      name?: string;
      config?: SandboxConfigBodyInput;
    }) => rpcClient.org.config.sandbox.update(input),
    onSuccess: () => {
      showToast({
        title: 'Configuration updated',
        message: 'Changes will apply to new sandbox executions.',
        color: 'success',
      });
      invalidate({});
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      showToast({
        title: 'Failed to update configuration',
        message: error.message,
        color: 'error',
      });
    },
  });
}

export function useDeleteSandboxConfig(options?: { onSuccess?: () => void }) {
  const invalidate = useInvalidateSandboxConfigs();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (id: string) => rpcClient.org.config.sandbox.delete({ id }),
    onSuccess: () => {
      showToast({
        title: 'Configuration deleted',
        message: 'Sandbox configuration has been removed.',
        color: 'success',
      });
      invalidate({});
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      showToast({
        title: 'Failed to delete configuration',
        message: error.message,
        color: 'error',
      });
    },
  });
}
